import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import Popup from "../../components/Popup";
import { parseSchemeImportRows } from "../../utils/schemeImport";
import {
    addEntry,
    bulkLoadFromCatalogue,
    checkCataloguePreview,
    deleteEntry,
    getSchemeById,
    getSchemeLookups,
    markEntryTaught,
    updateEntry,
    updateScheme,
} from "../../redux/schemeRelated/schemeHandle";
import { underSchemeControl } from "../../redux/schemeRelated/schemeSlice";

const WEEK_NUMBERS = {
    ONE: 1,
    TWO: 2,
    THREE: 3,
    FOUR: 4,
    FIVE: 5,
    SIX: 6,
    SEVEN: 7,
    EIGHT: 8,
    NINE: 9,
    TEN: 10,
    ELEVEN: 11,
    TWELVE: 12,
    THIRTEEN: 13,
    FOURTEEN: 14,
};

const WEEK_LABELS = Object.fromEntries(
    Object.entries(WEEK_NUMBERS).map(([label, num]) => [num, label])
);

const STATUS_CHIP = {
    Draft: "default",
    Submitted: "info",
    Approved: "success",
    Returned: "warning",
};

const DEFAULT_WEEK_LABELS = Object.keys(WEEK_NUMBERS);

const safeParse = (v) => {
    try {
        const parsed = JSON.parse(v);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

function weekLabel(num) {
    return WEEK_LABELS[num] || String(num);
}

function extractForm(className) {
    if (!className) return "";
    const m = className.match(/^(Form[1-4]|Std[1-8]|Year[1-5])/i);
    return m
        ? m[1].replace(/^form/i, "Form").replace(/^std/i, "Std").replace(/^year/i, "Year")
        : "";
}

function formatBlockDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function toDateInput(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
}

function BulletList({ text }) {
    const lines = String(text || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
    return (
        <Stack spacing={0.5}>
            {lines.map((line, i) => (
                <Typography key={i} variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    • {line.replace(/^[•-]\s*/, "")}
                </Typography>
            ))}
        </Stack>
    );
}

function LabelBlock({ label, children }) {
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

function ChipPicker({ options, selected, onToggle, disabled }) {
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", mx: -0.5 }}>
            {(options || []).map((opt) => {
                const isOn = selected.includes(opt);
                return (
                    <Chip
                        key={opt}
                        label={opt}
                        size="small"
                        variant={isOn ? "filled" : "outlined"}
                        color={isOn ? "primary" : "default"}
                        onClick={disabled ? undefined : () => onToggle(opt)}
                        sx={{ m: 0.5, cursor: disabled ? "default" : "pointer" }}
                    />
                );
            })}
        </Box>
    );
}

const SchemeDetail = () => {
    const { schemeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const { selectedScheme, lookupData, loading, statestatus, response, error, cataloguePreview } =
        useSelector((s) => s.scheme);

    const autoLoadRef = useRef(
        new URLSearchParams(location.search).get("loadTemplate") === "1"
    );
    const templateLoadPendingRef = useRef(false);
    const importInputRef = useRef(null);

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [addOpen, setAddOpen] = useState(false);
    const [loadTemplateOpen, setLoadTemplateOpen] = useState(false);
    const [loadConfirmText, setLoadConfirmText] = useState("");
    const [addTab, setAddTab] = useState(0);
    const [editingEntry, setEditingEntry] = useState(null);

    const [recordOpen, setRecordOpen] = useState(false);
    const [recordEntry, setRecordEntry] = useState(null);

    const [weekFromLabel, setWeekFromLabel] = useState("ONE");
    const [weekToLabel, setWeekToLabel] = useState("TWO");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [coreElement, setCoreElement] = useState("");
    const [topic, setTopic] = useState("");
    const [successCriteria, setSuccessCriteria] = useState("");
    const [plannedActivities, setPlannedActivities] = useState("");
    const [selectedMethods, setSelectedMethods] = useState([]);
    const [selectedResources, setSelectedResources] = useState([]);
    const [customResource, setCustomResource] = useState("");
    const [importing, setImporting] = useState(false);

    const [references, setReferences] = useState("");
    const [outcomes, setOutcomes] = useState("");
    const [remarks, setRemarks] = useState("");
    const [taughtDate, setTaughtDate] = useState(() => new Date().toISOString().slice(0, 10));

    const reload = useCallback(() => {
        if (schemeId) dispatch(getSchemeById(schemeId));
    }, [dispatch, schemeId]);

    useEffect(() => {
        if (schemeId) dispatch(getSchemeById(schemeId));
    }, [dispatch, schemeId]);

    useEffect(() => {
        const subName = selectedScheme?.subject?.subName;
        const form = extractForm(
            selectedScheme?.sclass?.sclassName || selectedScheme?.class?.sclassName
        );
        const term = selectedScheme?.term;
        if (subName) {
            dispatch(getSchemeLookups(subName, form, term));
        }
    }, [
        dispatch,
        selectedScheme?.subject?.subName,
        selectedScheme?.sclass?.sclassName,
        selectedScheme?.class?.sclassName,
        selectedScheme?.term,
    ]);

    useEffect(() => {
        if (statestatus === "added") {
            if (templateLoadPendingRef.current) {
                templateLoadPendingRef.current = false;
                const count = cataloguePreview?.entryCount ?? 0;
                setMessage(`Template loaded — ${count} week blocks added`);
                setLoadTemplateOpen(false);
                setLoadConfirmText("");
                setShowPopup(true);
            } else {
                setMessage("Saved successfully");
                setShowPopup(true);
                setAddOpen(false);
                setRecordOpen(false);
                setEditingEntry(null);
            }
            reload();
            dispatch(underSchemeControl());
            return;
        }
    }, [statestatus, dispatch, reload, cataloguePreview?.entryCount]);

    useEffect(() => {
        if (!selectedScheme || loading) return;

        const subName = selectedScheme.subject?.subName;
        const form = extractForm(
            selectedScheme.sclass?.sclassName || selectedScheme.class?.sclassName
        );
        const term = selectedScheme.term;
        if (subName && form && term) {
            dispatch(checkCataloguePreview(subName, form, term));
        }

        const entryList = Array.isArray(selectedScheme.entries) ? selectedScheme.entries : [];
        if (entryList.length === 0 && autoLoadRef.current && schemeId) {
            autoLoadRef.current = false;
            templateLoadPendingRef.current = true;
            dispatch(
                bulkLoadFromCatalogue(schemeId, {
                    subject: subName,
                    form,
                    term,
                    replaceExisting: false,
                })
            );
        }
    }, [selectedScheme, loading, schemeId, dispatch]);

    useEffect(() => {
        if (statestatus === "failed" || (response && statestatus !== "added")) {
            setMessage(typeof response === "string" ? response : "Request failed");
            setShowPopup(true);
            dispatch(underSchemeControl());
        }
    }, [response, statestatus, dispatch]);

    useEffect(() => {
        if (error && statestatus !== "added") {
            const msg =
                typeof error === "string" ? error : error?.message || "Network error";
            setMessage(msg);
            setShowPopup(true);
            dispatch(underSchemeControl());
        }
    }, [error, statestatus, dispatch]);

    const weekLabelOptions = lookupData?.weekLabels || DEFAULT_WEEK_LABELS;
    const coreElements = lookupData?.coreElements || [];
    const teachingMethodOptions = lookupData?.teachingMethods || [];
    const resourceOptions = lookupData?.commonResources || [];
    const textbookOptions = lookupData?.commonTextbooks || [];
    const picklistSource = lookupData?.picklistSource || "manual";
    const lookupScopeLabel =
        lookupData?.scopeLabel ||
        [lookupData?.subject, lookupData?.form, lookupData?.term].filter(Boolean).join(" · ");
    const useCoreElementPicklist = coreElements.length > 0;
    const isManualPicklists = picklistSource === "manual";

    const resetAddForm = () => {
        setWeekFromLabel("ONE");
        setWeekToLabel("TWO");
        setDateFrom("");
        setDateTo("");
        setCoreElement("");
        setTopic("");
        setSuccessCriteria("");
        setPlannedActivities("");
        setSelectedMethods([]);
        setSelectedResources([]);
        setAddTab(0);
        setEditingEntry(null);
    };

    const openAddDialog = () => {
        resetAddForm();
        setAddOpen(true);
    };

    const openEditDialog = (entry) => {
        setEditingEntry(entry);
        setWeekFromLabel(weekLabel(entry.weekFrom) || "ONE");
        setWeekToLabel(weekLabel(entry.weekTo) || "TWO");
        setDateFrom(toDateInput(entry.dateFrom));
        setDateTo(toDateInput(entry.dateTo));
        setCoreElement(entry.coreElement || "");
        setTopic(entry.topic || "");
        setSuccessCriteria(entry.successCriteria || "");
        setPlannedActivities(entry.plannedActivities || "");
        setSelectedMethods(safeParse(entry.teachingMethods));
        setSelectedResources(safeParse(entry.resources));
        setAddTab(0);
        setAddOpen(true);
    };

    const openRecordDialog = (entry) => {
        setRecordEntry(entry);
        setReferences(entry.references || "");
        setOutcomes(entry.outcomes || "");
        setRemarks(entry.remarks || "");
        setTaughtDate(toDateInput(entry.taughtDate) || new Date().toISOString().slice(0, 10));
        setRecordOpen(true);
    };

    const toggleMethod = (m) => {
        setSelectedMethods((prev) =>
            prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
        );
    };

    const toggleResource = (r) => {
        setSelectedResources((prev) =>
            prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
        );
    };

    const handleAddCustomResource = useCallback(() => {
        const value = customResource.trim();
        if (!value) return;
        setSelectedResources((prev) => (prev.includes(value) ? prev : [...prev, value]));
        setCustomResource("");
    }, [customResource]);

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file || !schemeId) return;
        try {
            const text = await file.text();
            const rows = parseSchemeImportRows(text);
            if (!rows.length) {
                throw new Error("No valid week blocks found in file.");
            }
            if (
                !window.confirm(
                    `Import ${rows.length} week block(s) into this scheme? Existing blocks are kept.`
                )
            ) {
                return;
            }
            setImporting(true);
            let imported = 0;
            for (const row of rows) {
                const core = row.coreElement || "General";
                await dispatch(
                    addEntry(schemeId, {
                        weekFrom: row.weekFrom,
                        weekTo: row.weekTo,
                        dateFrom: row.dateFrom,
                        dateTo: row.dateTo,
                        coreElement: core,
                        topic: row.topic,
                        successCriteria: row.successCriteria,
                        plannedActivities: row.plannedActivities,
                        teachingMethods: JSON.stringify(row.teachingMethods),
                        resources: JSON.stringify(row.resources),
                    })
                );
                imported += 1;
            }
            setMessage(`Imported ${imported} week block(s) from file`);
            setShowPopup(true);
            reload();
            dispatch(underSchemeControl());
        } catch (err) {
            setMessage(err?.message || "Could not import file");
            setShowPopup(true);
        } finally {
            setImporting(false);
        }
    };

    const appendTextbook = (book) => {
        const citation = book.full || book.short;
        if (!citation) return;
        setReferences((prev) => {
            if (prev.includes(citation)) return prev;
            return prev.trim() ? `${prev.trim()}\n${citation}` : citation;
        });
    };

    const handleSaveEntry = () => {
        const weekFrom = WEEK_NUMBERS[weekFromLabel];
        const weekTo = WEEK_NUMBERS[weekToLabel];
        if (!weekFrom || !weekTo) {
            setMessage("Week from and week to are required");
            setShowPopup(true);
            return;
        }
        if (weekTo < weekFrom) {
            setMessage("Week to must be on or after week from");
            setShowPopup(true);
            return;
        }
        if (!coreElement || !topic.trim()) {
            setMessage("Core element and topic are required");
            setShowPopup(true);
            return;
        }

        const payload = {
            weekFrom,
            weekTo,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            coreElement,
            topic: topic.trim(),
            successCriteria: successCriteria.trim(),
            plannedActivities: plannedActivities.trim(),
            teachingMethods: JSON.stringify(selectedMethods),
            resources: JSON.stringify(selectedResources),
        };

        const entryId = editingEntry?._id || editingEntry?.id;
        if (entryId) {
            dispatch(updateEntry(entryId, payload));
        } else {
            dispatch(addEntry(schemeId, payload));
        }
    };

    const handleSaveRecord = () => {
        const entryId = recordEntry?._id || recordEntry?.id;
        if (!entryId) return;
        dispatch(
            markEntryTaught(entryId, {
                references: references.trim(),
                outcomes: outcomes.trim(),
                remarks: remarks.trim(),
                taughtDate: taughtDate || new Date().toISOString().slice(0, 10),
            })
        );
    };

    const handleDeleteEntry = (entry) => {
        const id = entry._id || entry.id;
        if (!id) return;
        if (!window.confirm("Delete this week block?")) return;
        dispatch(deleteEntry(id));
    };

    const handleSubmitScheme = () => {
        if (!window.confirm("Submit this scheme for director approval?")) return;
        dispatch(updateScheme(schemeId, { status: "Submitted" }));
    };

    const handleLoadTemplate = () => {
        if (!schemeId || !scheme) return;
        const form = extractForm(scheme.sclass?.sclassName || scheme.class?.sclassName);
        templateLoadPendingRef.current = true;
        dispatch(
            bulkLoadFromCatalogue(schemeId, {
                subject: scheme.subject?.subName,
                form,
                term: scheme.term,
                replaceExisting: entries.length > 0,
            })
        );
    };

    const openLoadTemplateDialog = () => {
        setLoadConfirmText("");
        setLoadTemplateOpen(true);
    };

    const weekToOptions = useMemo(() => {
        const fromNum = WEEK_NUMBERS[weekFromLabel] || 1;
        return weekLabelOptions.filter((label) => (WEEK_NUMBERS[label] || 0) >= fromNum);
    }, [weekFromLabel, weekLabelOptions]);

    const scheme = selectedScheme;
    const status = scheme?.status || "Draft";
    const canEditScheme = status === "Draft" || status === "Returned";

    const entries = useMemo(() => {
        if (!scheme) return [];
        const list = Array.isArray(scheme.entries) ? [...scheme.entries] : [];
        return list.sort((a, b) => (a.sortOrder ?? a.weekFrom) - (b.sortOrder ?? b.weekFrom));
    }, [scheme]);

    const taughtCount = entries.filter((e) => e.isTaught).length;
    const totalEntries = entries.length;
    const coveragePct = totalEntries > 0 ? (taughtCount / totalEntries) * 100 : 0;
    const progressColor =
        coveragePct >= 80 ? "success" : coveragePct >= 50 ? "warning" : "error";

    const subjectName = scheme?.subject?.subName || "—";
    const className = scheme?.sclass?.sclassName || scheme?.class?.sclassName || "—";
    const schoolName = scheme?.school?.schoolName || "—";
    const schemeForm = extractForm(className);
    const showLoadTemplateButton =
        status === "Draft" && cataloguePreview?.hasTemplate === true;
    const topicPreviewList = cataloguePreview?.topicPreview || [];

    if (loading && !scheme) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
                <Skeleton variant="rounded" height={200} />
            </Container>
        );
    }

    if (!scheme && !loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Typography color="text.secondary">Scheme not found.</Typography>
                <Button sx={{ mt: 2 }} onClick={() => navigate("/Teacher/schemes")}>
                    Back to schemes
                </Button>
            </Container>
        );
    }

    return (
        <>
            <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                        <IconButton size="small" onClick={() => navigate("/Teacher/schemes")} sx={{ mt: 0.5 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ flex: 1, textAlign: "center" }}>
                            <Typography variant="h6" fontWeight={800}>
                                MINISTRY OF EDUCATION
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5 }}>
                                {schoolName}
                            </Typography>
                        </Box>
                        <Box sx={{ width: 40 }} />
                    </Stack>

                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2">
                                <strong>Subject:</strong> {subjectName} · <strong>Class:</strong> {className}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6} sx={{ textAlign: { md: "right" } }}>
                            <Typography variant="body2">
                                <strong>Term:</strong> {scheme.term} · <strong>Year:</strong> {scheme.academicYear} ·{" "}
                                <strong>Periods/week:</strong> {scheme.periodsPerWeek ?? 5}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1}
                        sx={{ mt: 2 }}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            Status:
                        </Typography>
                        <Chip label={status} size="small" color={STATUS_CHIP[status] || "default"} />
                        {canEditScheme ? (
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<SendOutlinedIcon />}
                                onClick={handleSubmitScheme}
                                disabled={loading || entries.length === 0}
                            >
                                Submit for Approval
                            </Button>
                        ) : null}
                        {showLoadTemplateButton ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AutoAwesomeIcon />}
                                onClick={openLoadTemplateDialog}
                                disabled={loading}
                            >
                                Load from template
                            </Button>
                        ) : null}
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        Scheme Coverage: {taughtCount} of {totalEntries} week blocks taught
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={coveragePct}
                        color={progressColor}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                </Paper>

                {canEditScheme ? (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openAddDialog}
                            disabled={importing}
                            sx={{ fontWeight: 700 }}
                        >
                            + Add Week Block
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileOutlinedIcon />}
                            onClick={() => importInputRef.current?.click()}
                            disabled={importing || loading}
                        >
                            {importing ? "Importing…" : "Import from file"}
                        </Button>
                        <input
                            ref={importInputRef}
                            type="file"
                            accept=".json,application/json"
                            hidden
                            onChange={handleImportFile}
                        />
                    </Stack>
                ) : null}

                {entries.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                            No week blocks yet. Add your first 2-week block, load a template when
                            available, or import a JSON file from your device.
                        </Typography>
                        {lookupScopeLabel && isManualPicklists && !cataloguePreview?.hasTemplate ? (
                            <Typography variant="body2" color="text.secondary">
                                No pre-loaded MIE data for <strong>{lookupScopeLabel}</strong> — type
                                fields manually or upload a scheme file.
                            </Typography>
                        ) : null}
                    </Paper>
                ) : (
                    entries.map((entry) => {
                        const entryId = entry._id || entry.id;
                        const methods = safeParse(entry.teachingMethods);
                        const resources = safeParse(entry.resources);
                        const dateRange =
                            entry.dateFrom || entry.dateTo
                                ? `${formatBlockDate(entry.dateFrom)} TO ${formatBlockDate(entry.dateTo)}`
                                : null;

                        return (
                            <Paper key={entryId} variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
                                <Box
                                    sx={{
                                        bgcolor: "grey.50",
                                        p: 1.5,
                                        borderBottom: 1,
                                        borderColor: "divider",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            WEEK {weekLabel(entry.weekFrom)}
                                            {entry.weekTo !== entry.weekFrom
                                                ? ` — ${weekLabel(entry.weekTo)}`
                                                : ""}
                                        </Typography>
                                        {dateRange ? (
                                            <Typography variant="caption" color="text.secondary">
                                                {dateRange}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                        <Chip
                                            size="small"
                                            label={entry.isTaught ? "Taught" : "Not Yet"}
                                            color={entry.isTaught ? "success" : "default"}
                                        />
                                        {!entry.isTaught ? (
                                            <>
                                                {canEditScheme ? (
                                                    <>
                                                        <Tooltip title="Edit block">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openEditDialog(entry)}
                                                            >
                                                                <EditOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete block">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteEntry(entry)}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                ) : null}
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => openRecordDialog(entry)}
                                                >
                                                    Mark as Taught
                                                </Button>
                                            </>
                                        ) : entry.isTaught ? (
                                            <Button size="small" onClick={() => openRecordDialog(entry)}>
                                                Edit Record
                                            </Button>
                                        ) : null}
                                    </Stack>
                                </Box>

                                <Box sx={{ p: 2 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight={700}
                                        display="block"
                                        sx={{ mb: 1.5 }}
                                    >
                                        SCHEME OF WORK
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={8}>
                                            <LabelBlock label="Core Element:">
                                                <Typography variant="body2">
                                                    {entry.coreElement || "—"}
                                                </Typography>
                                            </LabelBlock>
                                            <LabelBlock label="Topic:">
                                                <Typography variant="body1" fontWeight={500}>
                                                    {entry.topic || "—"}
                                                </Typography>
                                            </LabelBlock>
                                            <LabelBlock label="Success Criteria:">
                                                <BulletList text={entry.successCriteria} />
                                            </LabelBlock>
                                            <LabelBlock label="Planned Activities:">
                                                <BulletList text={entry.plannedActivities} />
                                            </LabelBlock>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <LabelBlock label="Teaching Methods:">
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {methods.length > 0 ? (
                                                        methods.map((m) => (
                                                            <Chip key={m} label={m} size="small" variant="outlined" />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            —
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </LabelBlock>
                                            <LabelBlock label="Resources:">
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {resources.length > 0 ? (
                                                        resources.map((r) => (
                                                            <Chip key={r} label={r} size="small" variant="outlined" />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            —
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </LabelBlock>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Divider />

                                <Box sx={{ p: 2, bgcolor: "action.hover" }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontWeight={700}
                                        display="block"
                                        sx={{ mb: 1 }}
                                    >
                                        RECORD OF WORK
                                    </Typography>
                                    {!entry.isTaught ? (
                                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                            Fill this section after teaching the topic
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1}>
                                            <LabelBlock label="References:">
                                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                                    {entry.references || "—"}
                                                </Typography>
                                            </LabelBlock>
                                            <LabelBlock label="Outcomes:">
                                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                                    {entry.outcomes || "—"}
                                                </Typography>
                                            </LabelBlock>
                                            <LabelBlock label="Remarks:">
                                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                                    {entry.remarks || "—"}
                                                </Typography>
                                            </LabelBlock>
                                        </Stack>
                                    )}
                                </Box>
                            </Paper>
                        );
                    })
                )}
            </Container>

            {/* Add / Edit Week Block */}
            <Dialog
                open={addOpen}
                onClose={() => {
                    setAddOpen(false);
                    resetAddForm();
                }}
                maxWidth="md"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>
                    {editingEntry ? "Edit Week Block" : "Add Week Block"} — Scheme of Work
                </DialogTitle>
                <DialogContent dividers>
                    <Tabs value={addTab} onChange={(_, v) => setAddTab(v)} sx={{ mb: 2 }}>
                        <Tab label="Scheme of Work" />
                        <Tab label="Record of Work" disabled />
                    </Tabs>
                    {addTab === 1 ? (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            Save the week block first, then use &quot;Mark as Taught&quot; to fill the Record of Work.
                        </Typography>
                    ) : (
                        <Stack spacing={3}>
                            {lookupScopeLabel ? (
                                <Alert
                                    severity={picklistSource === "catalogue" ? "success" : "info"}
                                    sx={{ borderRadius: 2 }}
                                >
                                    {picklistSource === "catalogue" ? (
                                        <>
                                            Picklists from the MIE template for{" "}
                                            <strong>{lookupScopeLabel}</strong>.
                                        </>
                                    ) : picklistSource === "subject" ? (
                                        <>
                                            Subject defaults for <strong>{lookupScopeLabel}</strong>.
                                            No pre-loaded template for this form and term — type topics
                                            manually or import a JSON file.
                                        </>
                                    ) : (
                                        <>
                                            No pre-loaded data for <strong>{lookupScopeLabel}</strong>.
                                            Type core elements and topics manually, or import week blocks
                                            from a JSON file on your device.
                                        </>
                                    )}
                                </Alert>
                            ) : null}
                            <Box>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                    Week & Dates
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Week From</InputLabel>
                                            <Select
                                                label="Week From"
                                                value={weekFromLabel}
                                                onChange={(e) => {
                                                    setWeekFromLabel(e.target.value);
                                                    const fromNum = WEEK_NUMBERS[e.target.value];
                                                    const toNum = WEEK_NUMBERS[weekToLabel];
                                                    if (toNum < fromNum) setWeekToLabel(e.target.value);
                                                }}
                                            >
                                                {weekLabelOptions.map((w) => (
                                                    <MenuItem key={w} value={w}>
                                                        {w}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Week To</InputLabel>
                                            <Select
                                                label="Week To"
                                                value={weekToLabel}
                                                onChange={(e) => setWeekToLabel(e.target.value)}
                                            >
                                                {weekToOptions.map((w) => (
                                                    <MenuItem key={w} value={w}>
                                                        {w}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Date From"
                                            type="date"
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Date To"
                                            type="date"
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                    Topic & Objectives
                                </Typography>
                                <Stack spacing={2}>
                                    {useCoreElementPicklist ? (
                                        <FormControl fullWidth required>
                                            <InputLabel>Core Element</InputLabel>
                                            <Select
                                                label="Core Element"
                                                value={coreElement}
                                                onChange={(e) => setCoreElement(e.target.value)}
                                            >
                                                {coreElements.map((el) => (
                                                    <MenuItem key={el} value={el}>
                                                        {el}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            required
                                            label="Core Element"
                                            value={coreElement}
                                            onChange={(e) => setCoreElement(e.target.value)}
                                            placeholder="e.g. Crop Production, Number and Numeration"
                                            helperText="Type the core element — no picklist for this subject, form, and term"
                                        />
                                    )}
                                    <TextField
                                        fullWidth
                                        required
                                        label="Topic"
                                        value={topic}
                                        onChange={(e) => setTopic(e.target.value)}
                                        helperText="e.g. Quadratic Equations, Circle Geometry, Surds"
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Success Criteria"
                                        value={successCriteria}
                                        onChange={(e) => setSuccessCriteria(e.target.value)}
                                        placeholder={
                                            "Students must be able to:\n• Factorize quadratic expressions\n• Solve quadratic equations\n• Apply to practical problems"
                                        }
                                        helperText="List what students should achieve — start each point on a new line"
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={6}
                                        label="Planned Activities"
                                        value={plannedActivities}
                                        onChange={(e) => setPlannedActivities(e.target.value)}
                                        placeholder={
                                            "Core Element: Number and Numeration\nTopic: Quadratic Equations\n• Discussing quadratic expressions\n• Factorizing quadratic expressions\n• Solving by factorization\n• Completing the square"
                                        }
                                        helperText="List activities the teacher and students will do — one per line"
                                    />
                                </Stack>
                            </Box>

                            <Box>
                                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                                    Teaching Methods
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    Click to select — choose all that apply
                                </Typography>
                                <ChipPicker
                                    options={teachingMethodOptions}
                                    selected={selectedMethods}
                                    onToggle={toggleMethod}
                                />
                            </Box>

                            <Box>
                                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                                    Teaching & Learning Resources
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    Click to select — choose all that apply
                                </Typography>
                                <ChipPicker
                                    options={resourceOptions}
                                    selected={selectedResources}
                                    onToggle={toggleResource}
                                />
                                {isManualPicklists || resourceOptions.length === 0 ? (
                                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Add resource"
                                            value={customResource}
                                            onChange={(e) => setCustomResource(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleAddCustomResource();
                                                }
                                            }}
                                            placeholder="Type a resource and press Add"
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={handleAddCustomResource}
                                            disabled={!customResource.trim()}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            Add
                                        </Button>
                                    </Stack>
                                ) : null}
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setAddOpen(false);
                            resetAddForm();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSaveEntry} disabled={loading || addTab !== 0}>
                        Save Week Block
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Record of Work */}
            <Dialog
                open={recordOpen}
                onClose={() => {
                    setRecordOpen(false);
                    setRecordEntry(null);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Record of Work — Mark as Taught</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This fills the Record of Work section. Fill it after the topic is taught.
                    </Alert>
                    <Stack spacing={2.5}>
                        <Box>
                            <Typography variant="body2" fontWeight={600}>
                                References / Textbooks used
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Click textbooks to add to references, then edit page numbers
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", mb: 1 }}>
                                {textbookOptions.map((book) => (
                                    <Chip
                                        key={book.short}
                                        label={book.short}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => appendTextbook(book)}
                                        sx={{ m: 0.5, cursor: "pointer" }}
                                    />
                                ))}
                            </Box>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={references}
                                onChange={(e) => setReferences(e.target.value)}
                                placeholder={
                                    "Syllabus for Mathematics, Form 3 & 4, MIE\nExcel & Succeed in Mathematics, BK3, pp 1-10"
                                }
                            />
                        </Box>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Outcomes achieved"
                            value={outcomes}
                            onChange={(e) => setOutcomes(e.target.value)}
                            placeholder="What was actually covered and achieved in these lessons..."
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Remarks"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder="e.g. Lesson well received. Students struggled with completing the square — will revise next week."
                        />
                        <TextField
                            fullWidth
                            label="Taught Date"
                            type="date"
                            value={taughtDate}
                            onChange={(e) => setTaughtDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRecordOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveRecord} disabled={loading}>
                        Save Record of Work
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={loadTemplateOpen}
                onClose={() => {
                    setLoadTemplateOpen(false);
                    setLoadConfirmText("");
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Load scheme template</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                        {entries.length === 0 ? (
                            <>
                                <Alert severity="info">
                                    This will load {cataloguePreview?.entryCount ?? 0} pre-built week blocks
                                    for {subjectName} {schemeForm} {scheme?.term} from the MIE/MANEB reference
                                    schemes.
                                </Alert>
                                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                    {topicPreviewList.slice(0, 5).map((item, index) => (
                                        <Typography key={index} component="li" variant="body2" sx={{ mb: 0.5 }}>
                                            {item.topic || "—"}
                                        </Typography>
                                    ))}
                                </Box>
                            </>
                        ) : (
                            <>
                                <Alert severity="warning">
                                    This scheme already has {entries.length} entries. Loading the template will
                                    REPLACE all existing entries.
                                </Alert>
                                <TextField
                                    fullWidth
                                    label='Type REPLACE to confirm'
                                    value={loadConfirmText}
                                    onChange={(e) => setLoadConfirmText(e.target.value)}
                                />
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setLoadTemplateOpen(false);
                            setLoadConfirmText("");
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleLoadTemplate}
                        disabled={
                            loading ||
                            (entries.length > 0 && loadConfirmText !== "REPLACE")
                        }
                    >
                        Load Template
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </>
    );
};

export default SchemeDetail;
